import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { Organization, Space, SpaceMeeting, UserMetadata, UserProfile } from "../models";
import { RoamingSettingsService } from "../services";
import { clone, first, has, initial, omit } from "lodash";

type views =
  | "default"
  | "create_space"
  | "add_to_existing_space"
  | "onboarding"
  | "settings"
  | "about";

// indicates the state of the add-in store.
type AddInState = {
  currentView: views;
  user: UserProfile | undefined;
  organizations: Organization[];
  meeting: SpaceMeeting | undefined;
  space: Space | undefined;
  selectedOrganizationId: string | undefined;
  selectedSpaceId: string | undefined;
  isDialogOpen: boolean;
  hasSeenHelp: boolean;
  onboardingProgress: {
    complete: boolean;
    initialized: boolean;
    plansComplete: boolean;
    profileComplete: boolean;
    organizationComplete: boolean;
  };
};

//indicates the actions that can be performed on the add-in store.
type AddInActions = {
  setCurrentView: (view: views) => void;
  setUser: (user: UserProfile | undefined) => void;
  setOrganizations: (organizations: Organization[]) => void;
  setSelectedOrganizationId: (id: string | undefined) => void;
  setSelectedSpaceId: (id: string | undefined) => void;
  setMeeting: (meeting: SpaceMeeting) => void;
  setSpace: (space: Space) => void;
  setIsDialogOpen: (isOpen: boolean) => void;
  setHasSeenHelp: (hasSeen: boolean) => void;

  removeUser: () => void;
  removeMeeting: () => void;
  removeSpace: () => void;
  resetOrganizations: () => void;
  resetSelectedOrganizationId: () => void;
  resetSelectedSpaceId: () => void;
  resetState: () => void;
};

// indicates the initial state of the add-in store.
const initialState: AddInState = {
  currentView: "default",
  user: undefined,
  organizations: [],
  meeting: undefined,
  space: undefined,
  selectedOrganizationId: undefined,
  selectedSpaceId: undefined,
  isDialogOpen: false,
  hasSeenHelp: false,
  onboardingProgress: {
    complete: false,
    initialized: false,
    plansComplete: false,
    profileComplete: false,
    organizationComplete: false,
  },
};

// init roaming settings service
export const cadchatAddInStorage = new RoamingSettingsService("cadchat-add-in-storage");

// create the add-in store
export const useAddInStore = create<AddInState & AddInActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        setUser: (user) => {
          // clone the initial state to avoid mutating it
          const onboardingProgress = clone(initialState.onboardingProgress);

          // if we have organizations then the user really only needs to
          // complete the user_profile steps of the onboarding process.
          const organizations = get().organizations;

          if (user && user?.metadata?.completed_onboarding) {
            const completedOnboarding = user?.metadata?.completed_onboarding ?? [];
            onboardingProgress.initialized = true;
            onboardingProgress.plansComplete = completedOnboarding.includes("plans");
            onboardingProgress.profileComplete = completedOnboarding.includes("user_profile");
            onboardingProgress.organizationComplete = completedOnboarding.includes("organization");
            onboardingProgress.complete =
              (completedOnboarding.includes("user_profile") &&
                completedOnboarding.includes("organization")) ||
              (organizations.length > 0 && completedOnboarding.includes("user_profile"));
          } else if (user) {
            /** set the initialized status to true since we have a user object now. */
            onboardingProgress.initialized = true;
          }

          // set the users state
          set({
            user,
            onboardingProgress,
          });
        },
        setCurrentView: (view) => set({ currentView: view }),
        setOrganizations: (organizations: Organization[]) => {
          set({ organizations });

          const selectedOrganizationId = get().selectedOrganizationId;
          if (selectedOrganizationId) {
            const selectedOrganization = organizations.find(
              (org) => org.id === selectedOrganizationId
            );
            if (!selectedOrganization) {
              set({ selectedOrganizationId: undefined });
            }
          } else if (organizations.length === 1 && !selectedOrganizationId) {
            // set the selected organization id to the first organization if there is only one.
            set({ selectedOrganizationId: first(organizations).id });
          }
        },
        setHasSeenHelp: (hasSeen: boolean) => set({ hasSeenHelp: hasSeen }),
        setSelectedSpaceId: (id) => set({ selectedSpaceId: id }),
        setSelectedOrganizationId: (id) => set({ selectedOrganizationId: id }),
        setMeeting: (meeting: SpaceMeeting) => set({ meeting }),
        setSpace: (space: Space) => set({ space }),
        setIsDialogOpen: (isOpen: boolean) => set({ isDialogOpen: isOpen }),
        removeUser: () => set({ user: undefined }),
        removeMeeting: () => set({ meeting: undefined }),
        removeSpace: () => set({ space: undefined }),
        resetOrganizations: () => set({ organizations: [] }),
        resetSelectedSpaceId: () => set({ selectedSpaceId: undefined }),
        resetSelectedOrganizationId: () => set({ selectedOrganizationId: undefined }),
        resetState: () => {
          set(omit(initialState, ["selectedOrganizationId", "hasSeenHelp"]));
        },
      }),
      {
        name: "add-in-storage",
        storage: createJSONStorage(() => cadchatAddInStorage),
        skipHydration: true,
        partialize: (state) => ({
          selectedOrganizationId: state.selectedOrganizationId,
          hasSeenHelp: state.hasSeenHelp,
        }),
      }
    )
  )
);

// rehydrate the state on Office ready
Office.onReady(async () => {
  if (cadchatAddInStorage.isRoamingAvailable()) {
    useAddInStore.persist.rehydrate();
  }
});
